import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelectedTeam } from './useSelectedTeam';
import liveChargingService from 'src/services/liveChargingService';

export const useLiveChargingStatus = (options = {}) => {
  const { teamId } = useSelectedTeam();
  const isMountedRef = useRef(true);
  const [data, setData] = useState({
    summary: {
      totalStations: 0,
      online: 0,
      charging: 0,
      offline: 0,
      maintenance: 0,
      lastUpdated: null
    },
    stations: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);

  // Fetch initial data
  const fetchData = useCallback(async () => {
    if (!teamId || !isMountedRef.current) return;

    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }
      
      const response = await liveChargingService.getLiveChargingStatus(teamId, {
        includeDetails: true,
        statusFilter: 'all'
      });
      
      if (isMountedRef.current) {
        if (response.success) {
          setData(response.data);
        } else {
          setError(new Error(response.message || 'Failed to fetch live charging status'));
        }
      }
    } catch (err) {
      console.error('Error fetching live charging status:', err);
      if (isMountedRef.current) {
        setError(err);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [teamId]);

  // Real-time updates disabled - data is refreshed manually

  // Real-time updates disabled - user must refresh manually
  // useEffect(() => {
  //   if (!teamId || !options.enableRealTime) return;
  //   // Real-time updates disabled
  // }, [teamId, options.enableRealTime, handleRealTimeUpdate, fetchData]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Manual refresh function
  const refresh = useCallback(() => {
    if (!teamId || !isMountedRef.current) return;

    const refreshData = async () => {
      try {
        if (isMountedRef.current) {
          setIsLoading(true);
          setError(null);
        }
        
        const response = await liveChargingService.getLiveChargingStatus(teamId, {
          includeDetails: true,
          statusFilter: 'all'
        });
        
        if (isMountedRef.current) {
          if (response.success) {
            setData(response.data);
          } else {
            setError(new Error(response.message || 'Failed to fetch live charging status'));
          }
        }
      } catch (err) {
        console.error('Error fetching live charging status:', err);
        if (isMountedRef.current) {
          setError(err);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    refreshData();
  }, [teamId]);

  // Update station status
  const updateStationStatus = useCallback(async (stationId, statusData) => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await liveChargingService.updateStationStatus(teamId, stationId, statusData);
      if (response.success) {
        // Refresh data after successful update
        const refreshResponse = await liveChargingService.getLiveChargingStatus(teamId, {
          includeDetails: true,
          statusFilter: 'all'
        });
        if (isMountedRef.current && refreshResponse.success) {
          setData(refreshResponse.data);
        }
        return response;
      } else {
        throw new Error(response.message || 'Failed to update station status');
      }
    } catch (err) {
      console.error('Error updating station status:', err);
      throw err;
    }
  }, [teamId]);

  // Get station details
  const getStationDetails = useCallback(async (stationId) => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await liveChargingService.getStationDetails(teamId, stationId);
      if (response.success) {
        if (isMountedRef.current) {
          setSelectedStation(response.data);
        }
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch station details');
      }
    } catch (err) {
      console.error('Error fetching station details:', err);
      throw err;
    }
  }, [teamId]);

  return {
    data,
    isLoading,
    error,
    selectedStation,
    setSelectedStation,
    refresh,
    updateStationStatus,
    getStationDetails
  };
};
